import React, { useState } from "react";
import { Camera, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TASK } from "@/constants/testIds";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

export default function PhotoUploader({ taskId, onAdded }) {
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error("Only JPG / PNG / GIF / WEBP images allowed");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Image is too large (max 5 MB)");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result || "");
    reader.readAsDataURL(f);
  };

  const send = async () => {
    if (!caption.trim() || !file) {
      toast.error("Add a caption and a photo");
      return;
    }
    setBusy(true);
    try {
      // 1) Upload the binary to object storage (returns a backend-served URL)
      const form = new FormData();
      form.append("file", file);
      // Let axios set the multipart Content-Type (with boundary) automatically.
      const { data: uploaded } = await api.post("/uploads", form);

      // 2) Attach the URL to the task's photo timeline
      const { data: photo } = await api.post(`/tasks/${taskId}/photos`, {
        caption,
        image_url: uploaded.url,
      });

      onAdded(photo);
      setCaption("");
      setFile(null);
      setPreview("");
      toast.success("Update posted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border-t border-[var(--eb-border)] pt-6">
      <div className="text-xs uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">Post update</div>
      <div className="mt-3 grid sm:grid-cols-3 gap-3">
        <label className="eb-elev p-3 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[var(--eb-primary)] min-h-[120px]">
          {preview ? (
            <img src={preview} alt="" className="w-full h-24 object-cover rounded" />
          ) : (
            <>
              <Upload className="w-5 h-5 text-[var(--eb-text-dim)]" />
              <span className="text-xs mt-2 text-[var(--eb-text-dim)]">Click to upload photo</span>
              <span className="text-[10px] mt-1 text-[var(--eb-text-faint)]">JPG · PNG · WEBP · max 5 MB</span>
            </>
          )}
          <input
            data-testid={TASK.photoFile}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={onFile}
            className="hidden"
          />
        </label>
        <input
          data-testid={TASK.photoCaption}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          className="eb-input sm:col-span-2"
          placeholder="Caption — e.g. 'Item purchased at Apple store, ₦10,500.'"
        />
      </div>
      <div className="mt-3 flex justify-end">
        <button
          data-testid={TASK.addPhotoBtn}
          onClick={send}
          disabled={busy}
          className="eb-btn eb-btn-primary !py-2 !px-4 !text-sm"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          {busy ? "Uploading…" : "Post photo update"}
        </button>
      </div>
    </div>
  );
}
